import React, {useMemo, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  VStack,
  Flex,
  useTheme,
  Spinner,
  Center,
  Container,
  AbsoluteCenter,
} from '@chakra-ui/react';
import {useParams, useNavigate} from 'react-router-dom';
import {useInfiniteGlobalSearchWithParamsAndLocation} from '../../hooks/useFetchGlobalSearch';
import useGetUserLocation from '../../hooks/useUserLocation';
import useAuth from '../../hooks/useAuth';
import OfferCard from '../../components/Articles/Offers/OfferCard';
import {TextA20, TextA16, TextA14, TextG14, TextG12} from '../../components/typography';
import {AnimateContent} from '../../routes/AuthorizedRoutes';
import TopBar from '../../components/TopBar';
import useTopBarOffset from '../../hooks/useTopBarOffset';
import {useMonthlyPerksSummary} from '../../hooks/useOffers';
import dayjs from 'dayjs';

function MonthlyPerks() {
  const theme = useTheme();
  const {month} = useParams<{month: 'current' | 'next'}>();
  const navigate = useNavigate();
  const userLocation = useGetUserLocation();
  const {user} = useAuth();
  const topOffset = useTopBarOffset();

  const isProduction = process.env.NODE_ENV === 'production';
  const insiderClubInterestId = isProduction ? '294' : '212';

  // Fetch monthly perks summary for total values
  const {data: perksSummary, isLoading: perksSummaryLoading} = useMonthlyPerksSummary();

  // Redirect if not a member
  useEffect(() => {
    if (user && !user.hasActiveMembership) {
      navigate('/memberships');
    }
  }, [user, navigate]);

  // Fetch offers for the specified month
  const fetchMonthlyOffers = useInfiniteGlobalSearchWithParamsAndLocation({
    type: 'offer',
    lat: userLocation?.data?.latitude,
    long: userLocation?.data?.longitude,
    'topics[]': insiderClubInterestId,
    term: '',
    orderBy: 'ends_at',
    sort: {by: 'ends_at', direction: 'asc'},
    month: month || 'current',
  });

  const monthlyOffers = fetchMonthlyOffers.data?.pages.flatMap((page) => page.data.data) || [];

  // Get total retail value from the summary endpoint
  const totalRetailPrice = useMemo(() => {
    if (!perksSummary?.data) return 0;
    const isNextMonth = month === 'next';
    return isNextMonth
      ? perksSummary.data.next_month?.total_retail_value ?? 0
      : perksSummary.data.current_month?.total_retail_value ?? 0;
  }, [perksSummary, month]);

  const perkCount = useMemo(() => {
    if (!perksSummary?.data) return 0;
    const isNextMonth = month === 'next';

    return isNextMonth
      ? perksSummary.data.count?.total_retail_value ?? 0
      : perksSummary.data.count?.total_retail_value ?? 0;
  }, [perksSummary, month]);

  const isNextMonth = month === 'next';

  const title = isNextMonth
    ? `Next Month's Perks: $${Math.floor(totalRetailPrice)} in Value`
    : `This Month's Perks: $${Math.floor(totalRetailPrice)} in Value`;

  // Show nothing while redirecting for non-members
  if (!user?.hasActiveMembership) {
    return <></>;
  }

  // IntersectionObserver for infinite scroll
  const observer = useRef<any>();
  const lastOfferElementRef = useCallback(
    (node) => {
      if (fetchMonthlyOffers.isLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && fetchMonthlyOffers.hasNextPage) {
          fetchMonthlyOffers.fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [fetchMonthlyOffers.isLoading, fetchMonthlyOffers.hasNextPage]
  );

  return (
    <AnimateContent>
      <TopBar />
      <Container pt={topOffset} pb={10} h="auto">
        {/* Value Card - Enhanced with gradient and visual elements */}
        <Box
          mt={4}
          mb={4}
          borderRadius="16px"
          padding="24px"
          background={
            isNextMonth
              ? 'linear-gradient(135deg, #fff5f7 0%, #ffe8ec 100%)'
              : 'linear-gradient(135deg, #f0f7ff 0%, #e6f2ff 100%)'
          }
          border={
            isNextMonth
              ? `2px solid ${theme.colors.rose}40`
              : `2px solid ${theme.colors.lightBlue}40`
          }
          boxShadow={
            isNextMonth
              ? `0 8px 24px rgba(224, 47, 132, 0.15), 0 2px 8px rgba(224, 47, 132, 0.1)`
              : `0 8px 24px rgba(66, 153, 225, 0.15), 0 2px 8px rgba(66, 153, 225, 0.1)`
          }
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-20%',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: isNextMonth
              ? 'radial-gradient(circle, rgba(224, 47, 132, 0.08) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(66, 153, 225, 0.08) 0%, transparent 70%)',
            filter: 'blur(40px)',
            zIndex: 0,
          }}>
          <VStack align="flex-start" spacing={3} position="relative" zIndex={1}>
            <Flex width="100%" justify="space-between" align="center" flexWrap="wrap" gap={3}>
              <Box>
                <TextA20 color={theme.colors.darkBlue} fontWeight="700" lineHeight="1.2">
                  {title}
                </TextA20>
              </Box>
              <Box
                px={5}
                py={2}
                borderRadius="full"
                background={
                  isNextMonth
                    ? 'linear-gradient(135deg, #ffd6e0 0%, #ffb3c6 100%)'
                    : 'linear-gradient(135deg, #cce5ff 0%, #b3d9ff 100%)'
                }
                border={
                  isNextMonth
                    ? `2px solid ${theme.colors.rose}60`
                    : `2px solid ${theme.colors.lightBlue}60`
                }
                boxShadow={
                  isNextMonth
                    ? '0 4px 12px rgba(224, 47, 132, 0.2)'
                    : '0 4px 12px rgba(66, 153, 225, 0.2)'
                }>
                <TextA16 color={theme.colors.darkBlue} fontWeight="700">
                  {isNextMonth
                    ? perksSummary?.data.next_month?.count
                    : perksSummary?.data.current_month?.count}{' '}
                  {monthlyOffers.length === 1 ? 'Perk' : 'Perks'}
                </TextA16>
              </Box>
            </Flex>
            <TextA14 color={theme.colors.grey} fontWeight="500" lineHeight="1.6">
              {isNextMonth
                ? `Preview exclusive perks available next month. These offers will unlock throughout next month.`
                : `Exclusive Insiders perks available this month. Redeem before they expire!`}
            </TextA14>
            {isNextMonth && (
              <Box
                mt={1}
                background="linear-gradient(135deg, #ffe8ec 0%, #ffd6e0 100%)"
                border={`2px solid ${theme.colors.rose}50`}
                borderRadius="12px"
                padding="14px 18px"
                width="100%"
                boxShadow="0 4px 12px rgba(224, 47, 132, 0.1)">
                <Flex align="center" gap={3}>
                  <TextA14 color={theme.colors.darkBlue} fontWeight="700">
                    🔒 Locked Until Next Month
                  </TextA14>
                </Flex>
              </Box>
            )}
          </VStack>
        </Box>

        {/* Offers Listing */}
        {(fetchMonthlyOffers.isLoading || perksSummaryLoading) && monthlyOffers.length === 0 ? (
          <Center pt={16}>
            <Spinner
              thickness="4px"
              speed="0.65s"
              emptyColor={theme.colors.lightGrey}
              color={theme.colors.lightBlue}
              size="lg"
            />
          </Center>
        ) : (
          <VStack spacing={4} align="stretch">
            {monthlyOffers.map((offer: any, index: number) => {
              const offerStartDate = offer.starts_at ? dayjs.utc(offer.starts_at) : null;

              // Always use the offer unlock date (starts_at), not the event date
              const date = offerStartDate ? offerStartDate.local().format('MMM DD') : '';

              // Check if offer is actually locked (starts in the future)
              const isLocked = offerStartDate ? offerStartDate.isAfter(dayjs()) : false;
              const shouldShowLockedOverlay = isNextMonth && isLocked;

              const isLastOffer = monthlyOffers.length === index + 1;

              return (
                <Box
                  key={offer.id}
                  ref={isLastOffer ? lastOfferElementRef : null}
                  position="relative"
                  opacity={shouldShowLockedOverlay ? 0.85 : 1}
                  filter={shouldShowLockedOverlay ? 'grayscale(20%)' : 'none'}>
                  <OfferCard
                    offer={offer}
                    fullCard={true}
                    showBookmark={true}
                    showExclusive={true}
                  />
                  {shouldShowLockedOverlay && (
                    <AbsoluteCenter zIndex={999} pointerEvents="none">
                      <Box
                        bg={theme.colors.rose}
                        color="white"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontWeight="700"
                        boxShadow="0 2px 8px rgba(0,0,0,0.3)"
                        zIndex={10}>
                        <TextG12>🔒 Locked until {date}</TextG12>
                      </Box>
                    </AbsoluteCenter>
                  )}
                </Box>
              );
            })}
            {fetchMonthlyOffers.isFetchingNextPage && (
              <Center py={4}>
                <Spinner
                  thickness="4px"
                  speed="0.65s"
                  emptyColor={theme.colors.lightGrey}
                  color={theme.colors.lightBlue}
                  size="md"
                />
              </Center>
            )}
          </VStack>
        )}

        {!fetchMonthlyOffers.isLoading && monthlyOffers.length === 0 && (
          <Center pt={16}>
            <VStack spacing={3}>
              <TextA16 color={theme.colors.grey} fontWeight="600">
                No perks available yet
              </TextA16>
              <TextG14 color={theme.colors.grey} textAlign="center">
                Check back soon for new exclusive perks!
              </TextG14>
            </VStack>
          </Center>
        )}
      </Container>
    </AnimateContent>
  );
}

export default MonthlyPerks;
